import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import useAuthToken from '../../hooks/useAuthToken';
import AppConstants from '../../AppConstants';
import { BsTrash } from 'react-icons/bs';
import { FaCreditCard } from 'react-icons/fa';
import './stripeStyles.scss';

// Dedicated axios instance for payment-method calls
const paymentAxios = axios.create();

export type AddPaymentMethodFormProps = {
  onAdded?: (paymentMethodId?: string) => void;
  onTokenExpired?: () => void; // notify parent when token is expired
  inline?: boolean; // if true, render without a <form> wrapper and use a non-submit button
  compact?: boolean; // if true, hide the saved cards gallery to keep the embed minimal
  enableSelection?: boolean; // show radios to select an existing method
  selectedMethodId?: string; // currently selected method id
  onSelectSavedMethod?: (paymentMethodId: string) => void; // notify parent when user chooses a saved method
  allowMakePrimary?: boolean; // show Make Primary buttons for saved methods
  listStyle?: 'grid' | 'list'; // how to render saved cards gallery
};

type SavedMethod = {
  id: string;
  brand?: string;
  last4?: string;
  exp_month?: number;
  exp_year?: number;
  isDefault?: boolean;
};

function normalizeSavedMethods(input: any[]): SavedMethod[] {
  if (!Array.isArray(input)) return [];
  return input.map((m: any) => {
    const card = m?.card || m?.cardDetails || m?.card_info || m?.details || {};
    const brand = m?.brand || card?.brand;
    const last4 = m?.last4 || card?.last4;
    const expMonthRaw = m?.exp_month ?? m?.expMonth ?? card?.exp_month ?? card?.expMonth;
    const expYearRaw = m?.exp_year ?? m?.expYear ?? card?.exp_year ?? card?.expYear;
    const exp_month = typeof expMonthRaw === 'string' ? parseInt(expMonthRaw, 10) : expMonthRaw;
    const exp_year = typeof expYearRaw === 'string' ? parseInt(expYearRaw, 10) : expYearRaw;
    return {
      id: m?.stripePaymentMethodId || m?.id || m?.paymentMethodId || m?.payment_method_id || '',
      brand,
      last4,
      exp_month,
      exp_year,
      isDefault: m?.isDefault || m?.default || m?.is_primary || m?.primary || false,
    } as SavedMethod;
  });
}

function isTokenExpiredError(error: any): boolean {
  return error?.response?.status === 401 || error?.response?.status === 403;
}

export function AddPaymentMethodForm({ onAdded, onTokenExpired, inline = false, compact = false, enableSelection = false, selectedMethodId, onSelectSavedMethod, allowMakePrimary = true, listStyle = 'grid' }: AddPaymentMethodFormProps = {}) {
  const stripe = useStripe();
  const elements = useElements();
  const { userToken, ready } = useAuthToken();

  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [methods, setMethods] = useState<SavedMethod[]>([]);
  const [loadingMethods, setLoadingMethods] = useState(false);
  const [tokenExpired, setTokenExpired] = useState(false);

  const disabled = useMemo(
    () => !ready || !stripe || !elements || !userToken || submitting || tokenExpired,
    [ready, stripe, elements, userToken, submitting, tokenExpired]
  );

  const onSubmit = useCallback(
    async (e?: React.FormEvent | React.MouseEvent) => {
      if (e && typeof (e as any).preventDefault === 'function') (e as any).preventDefault();
      if (e && typeof (e as any).stopPropagation === 'function') (e as any).stopPropagation();
      setMessage(null);
      setError(null);

      if (!stripe || !elements) return;
      if (!userToken) {
        setError('Missing token. Open this page via the app link.');
        return;
      }

      const card = elements.getElement(CardElement);
      if (!card) {
        setError('Card element is not ready.');
        return;
      }

      try {
        setSubmitting(true);

        // Tokenize to read brand/last4/exp for duplicate check
        const tokenRes = await stripe.createToken(card);
        if (tokenRes.error) {
          setError(tokenRes.error.message || 'Card validation failed.');
          return;
        }
        const tCard = tokenRes.token?.card;
        const newBrand = (tCard?.brand || '').toUpperCase();
        const newLast4 = tCard?.last4 || '';
        const newExpMonth = tCard?.exp_month;
        const newExpYear = tCard?.exp_year;
        const isDuplicate = methods.some((m) => {
          const mBrand = (m.brand || '').toUpperCase();
          const brandMatch = !!newBrand && !!mBrand && newBrand === mBrand;
          const last4Match = !!newLast4 && !!m.last4 && newLast4 === m.last4;
          const expMonthMatch = m.exp_month && newExpMonth ? m.exp_month === newExpMonth : true;
          const expYearMatch = m.exp_year && newExpYear ? m.exp_year === newExpYear : true;
          return brandMatch && last4Match && expMonthMatch && expYearMatch;
        });
        if (isDuplicate) {
          setError('This card already exists in your saved methods.');
          return;
        }

        // 1) Create SetupIntent
        const setupIntentRes = await paymentAxios.post(
          AppConstants.PAYMENTS_SETUP_INTENT_API,
          {},
          { headers: { Authorization: `Bearer ${userToken}` } }
        );

        const clientSecret: string | undefined = setupIntentRes.data?.data?.clientSecret;
        if (!clientSecret) {
          setError('Failed to initialize card setup.');
          return;
        }

        // 2) Confirm card setup
        const confirmRes = await stripe.confirmCardSetup(clientSecret, {
          payment_method: { card },
          // Ensure redirect-based 3DS returns to our page
          return_url: `${window.location.origin}/stripe/return?flow=pm`,
        });

        if (confirmRes.error) {
          setError(confirmRes.error.message || 'Failed to confirm card setup.');
          return;
        }

        const setupPaymentMethod = confirmRes.setupIntent?.payment_method as string | { id?: string } | undefined;
        const paymentMethodId = typeof setupPaymentMethod === 'string' ? setupPaymentMethod : setupPaymentMethod?.id;
        const setupIntentId = confirmRes.setupIntent?.id;
        if (!paymentMethodId) {
          setError('No payment method returned from setup.');
          return;
        }

        // 3) Attach/save the payment method
        const res = await paymentAxios.post(
          AppConstants.PAYMENTS_ADD_PAYMENT_METHOD_API,
          { paymentMethodId, setupIntentId },
          { headers: { Authorization: `Bearer ${userToken}` } }
        );

        if (res.status >= 200 && res.status < 300) {
          setMessage('Payment method added successfully.');
          // Make the newly added payment method the default/primary
          try {
            await paymentAxios.post(
              AppConstants.PAYMENTS_SET_DEFAULT_METHOD_API,
              { paymentMethodId },
              { headers: { Authorization: `Bearer ${userToken}` } }
            );
          } catch {}
          try { onAdded?.(paymentMethodId); } catch {}

          // Consistent navigation to return page (works for 3DS2 in-place)
          const url = new URL('/stripe/return', window.location.origin);
          url.searchParams.set('flow', 'pm');
          url.searchParams.set('setup_intent_client_secret', clientSecret);
          const siStatus = confirmRes.setupIntent?.status;
          if (siStatus) url.searchParams.set('redirect_status', String(siStatus));
          window.location.assign(url.toString());
          return;
        } else {
          setError('Failed to save the payment method.');
        }
      } catch (err: any) {
        if (isTokenExpiredError(err)) {
          setTokenExpired(true);
          try { onTokenExpired?.(); } catch {}
          setError(null);
        } else {
          const apiMessage = err?.response?.data?.message || err?.message;
          setError(apiMessage || 'Unexpected error.');
        }
      } finally {
        setSubmitting(false);
      }
    },
    [stripe, elements, userToken, methods, onTokenExpired]
  );

  // Load saved methods for duplicate checks and display
  useEffect(() => {
    const load = async () => {
      if (!ready || !userToken) return;
      setLoadingMethods(true);
      try {
        const res = await paymentAxios.get(AppConstants.PAYMENTS_LIST_METHODS_API, {
          headers: { Authorization: `Bearer ${userToken}` },
        });
        const payload = res.data;
        let rawList: any[] = [];
        if (Array.isArray(payload)) rawList = payload as any[];
        else if (Array.isArray(payload?.data?.paymentMethods)) rawList = payload.data.paymentMethods;
        else if (Array.isArray(payload?.data)) rawList = payload.data;
        else if (Array.isArray(payload?.paymentMethods)) rawList = payload.paymentMethods;
        else if (Array.isArray(payload?.methods)) rawList = payload.methods;
        const list = normalizeSavedMethods(rawList);
        setMethods(list);
      } catch (err: any) {
        if (isTokenExpiredError(err)) {
          setTokenExpired(true);
          try { onTokenExpired?.(); } catch {}
        }
      } finally {
        setLoadingMethods(false);
      }
    };
    load();
  }, [ready, userToken, onTokenExpired]);

  const Content = (
    <>
      <div style={{ 
        border: '1px solid #E7ECF1', 
        borderRadius: 12, 
        padding: 16, 
        background: '#fff',
        transition: 'border-color 0.3s ease, box-shadow 0.3s ease'
      }}
      onFocusCapture={(e) => {
        e.currentTarget.style.borderColor = '#08A45D';
        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(8, 164, 93, 0.1)';
      }}
      onBlurCapture={(e) => {
        e.currentTarget.style.borderColor = '#E7ECF1';
        e.currentTarget.style.boxShadow = 'none';
      }}
      >
        <CardElement
          options={{
            hidePostalCode: true,
            style: {
              base: {
                fontSize: '16px',
                color: '#1f2937',
                fontFamily: 'Gilroy-Regular, sans-serif',
                '::placeholder': { color: '#9ca3af' }
              },
              invalid: {
                color: '#dc3545'
              }
            }
          }}
        />
      </div>
      {inline ? (
        <button type="button" onClick={onSubmit} disabled={disabled} className="btn btn-primary w-100 mt-3 py-2 text-white fw-semibold">
          {submitting ? 'Saving…' : 'Save Payment Method'}
        </button>
      ) : (
        <button type="submit" disabled={disabled} className="btn btn-primary w-100 mt-3 py-2 text-white fw-semibold">
          {submitting ? 'Saving…' : 'Save Payment Method'}
        </button>
      )}

      {message && <p style={{ color: 'green', marginTop: 12 }}>{message}</p>}
      {error && <p style={{ color: 'crimson', marginTop: 12 }}>{error}</p>}

      {!compact && (
      <div style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <h5 style={{ margin: 0 }}>Saved Cards</h5>
          <small style={{ color: '#6b7280' }}>CVV is never stored or shown</small>
        </div>
        {loadingMethods ? (
          <p>Loading saved cards…</p>
        ) : !Array.isArray(methods) || methods.length === 0 ? (
          <p>No saved cards yet.</p>
        ) : (
          listStyle === 'list' ? (
            <div className="saved-cards-list mt-3">
              {methods.map((m, idx) => {
                const brand = (m.brand || 'Card').toUpperCase();
                const last4 = m.last4 || '';
                const exp = m.exp_month && m.exp_year ? `${m.exp_month}/${String(m.exp_year).slice(-2)}` : '--/--';
                const isSelected = selectedMethodId === m.id;
                return (
                  <div 
                    key={m.id} 
                    className="saved-card-item d-flex align-items-center p-3 mb-3" 
                    style={{ 
                      border: `2px solid ${isSelected ? '#08A45D' : '#E7ECF1'}`, 
                      borderRadius: '12px',
                      backgroundColor: isSelected ? 'rgba(8, 164, 93, 0.08)' : '#fff',
                      transition: 'all 0.2s ease',
                      cursor: enableSelection ? 'pointer' : 'default',
                      boxShadow: isSelected ? '0 4px 12px rgba(8, 164, 93, 0.2)' : 'none'
                    }}
                    onClick={() => {
                      if (enableSelection) {
                          onSelectSavedMethod?.(m.id);
                      }
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = '#08A45D';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(8, 164, 93, 0.1)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = '#E7ECF1';
                        e.currentTarget.style.boxShadow = 'none';
                      }
                    }}
                  >
                    {/* Left side - Custom Radio Button */}
                    {enableSelection && (
                      <div className="me-3 d-flex align-items-center">
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectSavedMethod?.(m.id);
                          }}
                          style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            border: `2px solid ${isSelected ? '#08A45D' : '#677289'}`,
                            backgroundColor: '#fff',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.borderColor = '#08A45D';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.borderColor = '#677289';
                            }
                          }}
                        >
                          {/* Inner dot when selected */}
                          <div
                            style={{
                              width: '10px',
                              height: '10px',
                              borderRadius: '50%',
                              backgroundColor: isSelected ? '#08A45D' : 'transparent',
                              transition: 'all 0.2s ease',
                              display: 'block'
                            }}
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* Card Icon */}
                    <div className="me-3">
                      <FaCreditCard 
                        size={24} 
                        style={{ color: '#08A45D' }} 
                      />
                    </div>
                    
                    {/* Main Content */}
                    <div className="flex-grow-1 mb-0">
                      <div className="d-flex align-items-center mb-0">
                        <span className="fw-bold me-2 text-info" style={{ fontSize: '16px', color: '#1f2937' }}>
                          {brand} •••• {last4}
                        </span>
                        {m.isDefault && (
                          <span className="badge" style={{ 
                            backgroundColor: '#D9FBEC', 
                            color: '#08A45D',
                            fontSize: '12px' 
                          }}>
                            Primary
                          </span>
                        )}
                      </div>
                      
                      {/* Expires Date */}
                      <div className="text-muted mb-0" style={{ fontSize: '14px' }}>
                        Expires: {exp}
                      </div>
                      
                      {/* Set as Primary */}
                      {allowMakePrimary && !m.isDefault && (
                        <button
                          type="button"
                          className="btn btn-link p-0 fw-bold"
                          style={{ 
                            fontSize: '14px', 
                            color: '#08A45D',
                            textDecoration: 'none'
                          }}
                          onClick={async (e) => {
                            e.stopPropagation(); // Prevent card selection when clicking this button
                            try {
                              await paymentAxios.post(
                                AppConstants.PAYMENTS_SET_DEFAULT_METHOD_API,
                                { paymentMethodId: m.id },
                                { headers: { Authorization: `Bearer ${userToken}` } }
                              );
                              setMethods(prev => prev.map(x => ({ ...x, isDefault: x.id === m.id })));
                            } catch {}
                          }}
                        >
                          Set as Primary
                        </button>
                      )}
                    </div>
                    
                    {/* Right side - Delete Icon */}
                    <div className="ms-3">
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        style={{ 
                          border: 'none',
                          backgroundColor: 'transparent',
                          color: '#dc3545',
                          padding: '6px'
                        }}
                        onClick={async (e) => {
                          e.stopPropagation(); // Prevent card selection when clicking delete
                          try {
                            await paymentAxios.delete(
                              `${AppConstants.PAYMENTS_DELETE_METHOD_API}/${m.id}`,
                              { headers: { Authorization: `Bearer ${userToken}` } }
                            );
                            setMethods(prev => prev.filter(x => x.id !== m.id));
                          } catch {}
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#dc3545';
                          e.currentTarget.style.color = '#fff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = '#dc3545';
                        }}
                      >
                        <BsTrash size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="stripe-card-list">
              {methods.map((m) => {
                const brand = (m.brand || 'Card').toUpperCase();
                const last4 = m.last4 || '';
                const exp = m.exp_month && m.exp_year ? `${m.exp_month}/${String(m.exp_year).slice(-2)}` : '--/--';
                const isSelected = selectedMethodId === m.id;
                return (
                  <label className={`stripe-card-item brand-${brand.toLowerCase()}`} key={m.id} style={{ cursor: enableSelection ? 'pointer' : 'default' }}>
                    {enableSelection && (
                      <input
                        type="radio"
                        name="saved-card-select"
                        checked={!!isSelected}
                        onChange={() => onSelectSavedMethod?.(m.id)}
                        style={{ position: 'absolute', marginTop: 8, marginLeft: 8 }}
                      />
                    )}
                    <div className="stripe-card-top">
                      <div className="stripe-card-chip" />
                      <div className="stripe-card-brand">{brand}</div>
                    </div>
                    <div className="stripe-card-number">•••• •••• •••• {last4}</div>
                    <div className="stripe-card-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div className="stripe-card-label">Expiry</div>
                        <div className="stripe-card-exp">{exp}</div>
                      </div>
                      {allowMakePrimary && !m.isDefault && (
                        <button
                          type="button"
                          className="btn btn-outline-light btn-sm"
                          onClick={async () => {
                            try {
                              await paymentAxios.post(
                                AppConstants.PAYMENTS_SET_DEFAULT_METHOD_API,
                                { paymentMethodId: m.id },
                                { headers: { Authorization: `Bearer ${userToken}` } }
                              );
                              setMethods(prev => prev.map(x => ({ ...x, isDefault: x.id === m.id })));
                            } catch {}
                          }}
                        >
                          Make Primary
                        </button>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          )
        )}
      </div>
      )}
    </>
  );

  if (inline) {
    // Avoid nested <form> when embedded inside another form
    return <div>{Content}</div>;
  }

  return <form onSubmit={onSubmit}>{Content}</form>;
}

export default AddPaymentMethodForm;
